import { useState } from "react";
import { Card } from "../../../components/card.component";
import { Button } from "../../../components/button.component";
import {
  ModalGeneric,
  ModalTemplateDownload,
  IModalGenericProps,
} from "../../../components/modal";
import { FileType } from "rsuite/esm/Uploader/Uploader";
import { FileLoader } from "../../../components";
import { TypeTemplate } from "../../../config";
import { TemplatePicker, TemplatePreviewer } from "../../../components/import";
import { UtilityTemplate, UtilityWorkBook } from "../../../utils";
import { AppRouteApi } from "../../../config/appRoute";
import { ApiDataImport } from "../../../core/apis";
import { showToast } from "../../../components/toast.component";
import { WorkBook } from "xlsx";
import { Modal } from "rsuite";

const modalConfig: IModalGenericProps = {
  header: {
    element: (
      <Modal.Title>
        <p className="text-danger fw-bold message">
          Please check errors by hovering on highlighted cells
        </p>
      </Modal.Title>
    ),
  },
  footer: { visibility: false },
};

export function PageImport() {
  const [files, setFiles] = useState<FileType[]>([]);
  const [workbook, setWorkbook] = useState<WorkBook | null>(null);
  const [templateType, setTemplateType] = useState<TypeTemplate>();
  const [showModal, setShowModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [validationStatus, setValidationStatus] = useState({
    isValidating: false,
    haveError: false,
    isChecked: false,
  });
  const [tableData, setTableData] = useState<Object[]>([]);
  const [errors, setErrors] = useState<Object[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = (fileList: FileType[]) => {
    const file = fileList.pop();
    if (file) {
      setFiles([file]);
    } else {
      setFiles([]);
    }
    setValidationStatus({
      ...validationStatus,
      isChecked: false,
      haveError: false,
    });
    setTableData([]);
    setErrors([]);
  };
  const handleTemplateChange = (value: TypeTemplate) => {
    setTemplateType(value);
  };
  const handleToggleModal = () => {
    setShowModal(!showModal);
  };
  const handleToggleTemplateModal = () => {
    setShowTemplateModal(!showTemplateModal);
  };

  const resetData = () => {
    setFiles([]);
    setValidationStatus({
      isValidating: false,
      haveError: false,
      isChecked: false,
    });
    setTableData([]);
    setErrors([]);
    setWorkbook(null);
  };

  const convertDataToJSON = async ({
    haveToConvertType,
    givenWorkbook,
  }: {
    haveToConvertType: boolean;
    givenWorkbook?: WorkBook;
  }) => {
    if (!workbook && !givenWorkbook) {
      return;
    }
    return await UtilityTemplate.convertDataToJSONForApi(
      workbook || givenWorkbook!,
      templateType!,
      haveToConvertType
    );
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const data = await convertDataToJSON({ haveToConvertType: true });
      const apiURL =
        templateType === "Caregiver"
          ? AppRouteApi.Import.Caregiver()
          : AppRouteApi.Import.Patient();

      await ApiDataImport.saveTemplateData(apiURL, data);
      resetData();
    } catch (error: any) {
      console.error(error.message);
    }
    setIsLoading(false);
  };

  const handleValidate = async () => {
    if (!files[0]?.blobFile) {
      return;
    }
    setValidationStatus({ ...validationStatus, isValidating: true });
    try {
      const workbook = await UtilityWorkBook.getWorkbook(files[0].blobFile);
      const mainSheet = workbook.Sheets[workbook.SheetNames[0]];
      const secondSheet = workbook.Sheets[workbook.SheetNames[1]];

      const haveCorrectColumn = UtilityWorkBook.validateTemplateType(
        mainSheet,
        templateType!
      );

      if (!haveCorrectColumn) {
        showToast(
          "Column didn't match. Make sure template and selected template type is correct.",
          "error",
          "topCenter",
          0,
          true
        );
        setValidationStatus({ ...validationStatus, isValidating: false });
        return;
      }

      if (templateType === "Patient") {
        const insuranceTypes = await UtilityTemplate.convertDataToJSON(
          secondSheet,
          "Insurance",
          false
        );
        const insuranceIdList = insuranceTypes?.map(
          (insurance) => insurance.insurance_id
        );
        window.sessionStorage.setItem(
          "insuranceTypes",
          JSON.stringify(insuranceIdList)
        );
      }
      let { errors, haveError } = UtilityWorkBook.validateValue(
        mainSheet,
        templateType!
      );

      const data = await convertDataToJSON({
        haveToConvertType: false,
        givenWorkbook: workbook,
      });
      if (data) {
        setTableData(data as Object[]);
        const { updatedErrors, haveRequiredValueError } =
          UtilityTemplate.getMissingRequiredValueStatus({
            errors,
            data: data as Object[],
            templateType: templateType!,
          });
        errors = updatedErrors;
        haveError = haveError || haveRequiredValueError;
      }
      setErrors(errors);
      if (haveError) {
        setValidationStatus({
          ...validationStatus,
          isValidating: false,
          isChecked: true,
          haveError: true,
        });
        handleToggleTemplateModal();
      } else {
        setValidationStatus({
          ...validationStatus,
          isValidating: false,
          isChecked: true,
          haveError: false,
        });
        setWorkbook(workbook);
      }
    } catch (error: any) {
      console.error(error.message);
      const errorMessage = error.message;
      if (errorMessage?.includes("file could not be read")) {
        showToast(
          "File couldn't be read. Permission error occurred or File might have changed from previous load. Please remove current file and load again the file",
          "error",
          "topCenter",
          0,
          true
        );
      }
      setValidationStatus({
        ...validationStatus,
        isValidating: false,
        isChecked: true,
        haveError: true,
      });
    }
  };

  return (
    <div>
      <Card
        header={
          <div className="row align-items-center justify-content-between">
            <div className="col-md-8 col-12 content-box-title">
              <div>
                <p className="text-capitalize my-1 text-green-200">Import</p>
              </div>
            </div>
          </div>
        }
      >
        <Button
          name="Get Template"
          className="btn-green-subtle btn-sm mb-2 shadow-sm"
          onClick={handleToggleModal}
        />
        <FileLoader
          onChange={handleFileChange}
          fileList={files}
          accept=".xlsx, .csv, .xls"
          message="Click or drag a .xls or .xlsx or .csv file in this area to load"
          draggable
        />
        <div className="row justify-content-center">
          <div className="col col-lg-6">
            <TemplatePicker handleChange={handleTemplateChange} />
          </div>
        </div>
        <div className="text-center p-3">
          {validationStatus?.isChecked && !validationStatus.haveError ? (
            <Button
              name="Save"
              className="btn m-1 btn-green"
              style={{ width: "160px" }}
              loading={isLoading}
              onClick={handleSave}
              disabled={isLoading || !files?.length || !templateType}
            />
          ) : (
            <Button
              name="Validate File"
              className="btn-warning btn-sm"
              style={{ width: "160px" }}
              loading={validationStatus.isValidating}
              disabled={!templateType || !files.length}
              onClick={handleValidate}
            />
          )}
        </div>
      </Card>

      {validationStatus.haveError && (
        <ModalGeneric
          {...modalConfig}
          size="lg"
          backdrop="static"
          className="template-preview"
          open={showTemplateModal}
          onClose={handleToggleTemplateModal}
        >
          <TemplatePreviewer
            templateType={templateType!}
            data={tableData}
            errors={errors}
          />
        </ModalGeneric>
      )}
      {showModal && <ModalTemplateDownload handleClose={handleToggleModal} />}
    </div>
  );
}
